import { useEffect, useRef } from 'react';
import { UseFormWatch, UseFormSetValue, UseFormReset } from 'react-hook-form';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import { revaluation as rules } from 'further-ui/validations';
import {
  InvestmentHistory,
  Revaluation,
} from 'routes/LpPages/Company/Revaluations/types';
import { HoldingClassInvestmentTotal } from 'routes/LpPages/Company/Revaluations/CreateRevaluation/types';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { sumBy } from 'lodash';
import { updateAllNewRevaluationsUnitPrice } from 'routes/LpPages/Company/Revaluations/hooks/useRevaluationFormHandlers/newRevaluationBulkUpdates';
import revaluationQueriesFactory from 'routes/LpPages/Company/Revaluations/hooks/useRevaluations/revaluationQueriesFactory';

type UseInvestmentRevaluationSyncProps = {
  revaluationOption?: InvestmentRevaluationOption;
  holdingClasses: Array<{
    holdingClassId?: string;
    investmentAmountTcy: number;
    numberOfUnits: number;
    currency: CurrencyCode;
    unitPriceTcy: number;
  }>;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  investmentHistory: InvestmentHistory;
  watch: UseFormWatch<rules.RevaluationFormType>;
  setValue: UseFormSetValue<rules.RevaluationFormType>;
  reset: UseFormReset<rules.RevaluationFormType>;
};

const handleAllSharesRevaluation = (
  holdingClasses: Array<{
    holdingClassId?: string;
    investmentAmountTcy: number;
    numberOfUnits: number;
    currency: CurrencyCode;
    unitPriceTcy: number;
  }>,
  investmentHistory: InvestmentHistory,
  currentNewRevaluations: Array<Omit<Revaluation, 'revaluationId'>>,
  setValue: UseFormSetValue<rules.RevaluationFormType>,
) => {
  const totalInvestmentAmount = sumBy(holdingClasses, 'investmentAmountTcy');
  const totalUnitsFromNewInvestment = sumBy(holdingClasses, 'numberOfUnits');
  const averageUnitPrice = totalInvestmentAmount / totalUnitsFromNewInvestment;
  const totalUnitsFromAllHoldings = sumBy(
    investmentHistory,
    'totalNumberOfUnits',
  );

  setValue('currentUnitPrice', averageUnitPrice, {
    shouldValidate: true,
    shouldDirty: true,
  });

  const totalHoldValue =
    totalUnitsFromAllHoldings > 0
      ? averageUnitPrice * totalUnitsFromAllHoldings
      : 0;

  setValue('currentHoldValue', totalHoldValue, {
    shouldValidate: true,
    shouldDirty: true,
  });

  updateAllNewRevaluationsUnitPrice(
    averageUnitPrice,
    currentNewRevaluations,
    investmentHistory,
    setValue,
  );
};

const handleShareClassOnlyRevaluation = (
  holdingClasses: Array<{
    holdingClassId?: string;
    investmentAmountTcy: number;
    numberOfUnits: number;
    currency: CurrencyCode;
    unitPriceTcy: number;
  }>,
  investmentHistory: InvestmentHistory,
  currentNewRevaluations: Array<Omit<Revaluation, 'revaluationId'>>,
  setValue: UseFormSetValue<rules.RevaluationFormType>,
  reset: UseFormReset<rules.RevaluationFormType>,
) => {
  reset();
  const { getUnitsPerHoldingClass } = revaluationQueriesFactory({
    investmentHistory,
    newRevaluations: currentNewRevaluations,
    revaluations: [],
    revaluationFormFields: [],
  });

  holdingClasses.forEach((holdingClass) => {
    const holdingClassId = holdingClass.holdingClassId;

    if (!holdingClassId) {
      return;
    }

    const revaluationIndex = currentNewRevaluations.findIndex(
      (rev) => rev.holdingClassId === holdingClassId,
    );

    if (revaluationIndex === -1) {
      return;
    }

    setValue(
      `newRevaluations.${revaluationIndex}.unitPriceTcy`,
      holdingClass.unitPriceTcy,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );

    const numberOfUnits = getUnitsPerHoldingClass(
      investmentHistory,
      holdingClassId,
    );

    if (numberOfUnits > 0) {
      const holdValue = holdingClass.unitPriceTcy * numberOfUnits;
      setValue(
        `newRevaluations.${revaluationIndex}.currentHoldValue`,
        holdValue,
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    }
  });
};

export const useInvestmentRevaluationSync = ({
  revaluationOption,
  holdingClasses,
  holdingClassInvestmentTotals,
  investmentHistory,
  watch,
  setValue,
  reset,
}: UseInvestmentRevaluationSyncProps) => {
  const previousRevaluationOptionRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      revaluationOption === previousRevaluationOptionRef.current ||
      !revaluationOption
    ) {
      return;
    }

    previousRevaluationOptionRef.current = revaluationOption;

    if (!holdingClasses || holdingClasses.length === 0) {
      return;
    }

    const currentNewRevaluations = watch('newRevaluations') ?? [];

    if (revaluationOption === InvestmentRevaluationOption.AllShares) {
      handleAllSharesRevaluation(
        holdingClasses,
        investmentHistory,
        currentNewRevaluations,
        setValue,
      );
    } else if (
      revaluationOption === InvestmentRevaluationOption.ShareClassOnly
    ) {
      handleShareClassOnlyRevaluation(
        holdingClasses,
        investmentHistory,
        currentNewRevaluations,
        setValue,
        reset,
      );
    } else if (
      revaluationOption === InvestmentRevaluationOption.ManuallyInput
    ) {
      reset();
    }
  }, [
    revaluationOption,
    holdingClasses,
    investmentHistory,
    holdingClassInvestmentTotals,
    watch,
    setValue,
    reset,
  ]);
};
